/**
 * CO 576 phase 2 — the content pages' hero copy, single source.
 *
 * Consumed by BOTH the page components (`TrustPage.tsx` and friends, the
 * rendered hero) and `src/static-shells.ts` (the pre-hydration shell a reader
 * without JavaScript gets). One copy of the copy: editing here changes both
 * surfaces, and there is no second rendering that can drift.
 *
 * The CO 523 pattern, applied to nine more pages. CO 558 solved `/investors`
 * with a hand-written HTML fragment plus a test proving every sentence in it
 * appears in the page source — necessary, because that fragment IS a second
 * copy. At nine pages that would be nine fragments to keep in step, so the
 * copy moves here instead and drift becomes impossible by construction rather
 * than by assertion.
 *
 * ## Why these nine
 *
 * Measured 2026-09-22 by comparing each route's MD5 to `/`: twenty routes
 * served byte-identical homepage HTML. `llms.txt` advertises fifteen
 * destinations and thirteen were among them — our own machine-readable index
 * pointing AI readers at the home page. The five legal pages were phase 1
 * (their markdown was already a single source). These nine are the rest of
 * what `llms.txt` advertises.
 *
 * ## What belongs here
 *
 * The hero only — eyebrow, headline, lead. That is what a link preview shows,
 * what a search result shows, and what tells a reader which page they are on.
 * The body stays in the component: a shell is an identification, not a mirror
 * of the whole page, and pulling every section here would make this file the
 * page.
 */

export interface PageHero {
  /** Small line above the headline. */
  eyebrow: string;
  /** The page's own h1. */
  h1: string;
  /** The lead paragraph — the description a search result and link preview show. */
  lead: string;
  /** Browser tab and og:title. Kept short; the h1 is often a full sentence. */
  title: string;
  /**
   * The page's own section headings, in order.
   *
   * ⚠️ Parsed from the component's `<SectionHead title="…">` props by
   * `scripts/generate-page-heroes.ts` — NOT hand-written here, so they cannot
   * drift from the rendered page. Coverage is uneven by design: `/honesty` and
   * `/agent-ready` build their sections a different way and legitimately have
   * none, so there is no minimum. CO 523's homepage generator DOES enforce a
   * floor, because a homepage with zero parsed sections means the parser
   * broke; here zero is a real answer.
   */
  sections?: string[];
}

/** Route slug (no leading slash) → hero copy. */
export const PAGE_HEROES: Record<string, PageHero> = {
  trust: {
    eyebrow: 'Infrastructure you can verify',
    h1: "Don't trust the CEO. Read the evidence.",
    lead:
      "Most companies ask you to believe their claims. ComOS is built so you don't have to. " +
      "Trust here isn't a value on a slide — it's a mechanism, running continuously, observable " +
      'as a live rate, and decaying the moment it stops.',
    title: 'Trust',
  },
  honesty: {
    eyebrow: 'The honesty ratio · live',
    h1: "Here's the real number. Not a polished one.",
    lead:
      "Most companies show you the metrics that flatter them. This one we can't tune for a slide — " +
      "it's computed by the system, about the system, and published whether it's good news or not. " +
      "It's the fraction of the platform's own change record that has passed QC.",
    title: 'The honesty ratio',
  },
  security: {
    eyebrow: 'Security',
    h1: 'Autonomous, accountable, and locked down by default.',
    lead:
      'Agents act on your business, so the controls around them are the product, not an ' +
      'afterthought. Every action is scoped, logged, and reversible — and the standards an ' +
      'enterprise asks for are built in.',
    title: 'Security',
  },
  durability: {
    eyebrow: 'Built to last',
    h1: "You're betting your business on this. Here's why the bet is safe.",
    lead:
      'Running your whole operation on an OS means one question comes before all the others: will ' +
      "it still be here, and will it stay up while it is? The answer isn't a promise — it's the " +
      'infrastructure. No single box to fall over, no single database to lose.',
    title: 'Durability',
  },
  pricing: {
    eyebrow: 'A different model',
    h1: 'You pay for the business you do. Nothing for sitting still.',
    lead:
      'No subscription. No rent. ComOS takes a small, transparent cut of the money moving through ' +
      "your business — out and in — and nothing when you're idle.",
    title: 'Pricing',
  },
  install: {
    eyebrow: 'Run your own node',
    h1: 'Your whole business, on a machine you own.',
    lead:
      'A ComOS node is your world moved home: your catalog, your customers, your calendar, your ' +
      'history, and the agents that run the place — on your own hardware, in a room you can lock. ' +
      'The network stays what it was: one currency, one settlement system.',
    title: 'Install a node',
  },
  // CO 613 — /prompts is a header destination, so it serves its own title and
  // shell like every other advertised page (CO 576).
  prompts: {
    eyebrow: 'The prompt library',
    h1: "There's a prompt for that.",
    lead:
      'Paste one into your agent and it opens a rental shop or sets up your blog. Every prompt ' +
      'here is graded, marked proven only after a journaled run against a live tenant, and ' +
      'shows the tools and scopes it needs before you run it.',
    title: 'Prompts',
  },
  // CO 680 Phase 2b — the theme library page, built like /prompts: swatches
  // from the house supply, standing read live.
  themes: {
    eyebrow: 'The theme library',
    h1: 'Every theme, and whether it is proven.',
    lead:
      'The house themes ComOS ships, the ones others register, and the ones a store has put ' +
      'on its own pages — each shown in light and dark, and marked proven only while a stored ' +
      'proof matches the exact bytes being served.',
    title: 'Themes',
  },
  'agent-ready': {
    eyebrow: 'Agent-Ready Score',
    h1: 'How agent-ready is your store?',
    lead:
      "Enter a store URL. We'll check 10 things AI shopping agents need to find, understand, and " +
      'transact on your store today.',
    title: 'Agent Readiness Check',
  },
  docs: {
    eyebrow: 'Docs',
    h1: 'The ComOS spec.',
    lead:
      'One endpoint, every verb. Quick-start for agent builders, full reference per platform, and ' +
      'the concepts that make the OS the OS.',
    title: 'Docs',
  },
  platforms: {
    eyebrow: 'Your business, any platform',
    h1: 'The tenant is the spine. The platforms are your choice.',
    lead:
      'Every ComOS tenant starts with a complete core — identity, accounts, agents, governance, ' +
      'voice, knowledge. Then you pick what your business is — a store, a dentist, a kitchen, an ' +
      'auto shop — and add the tools to run it.',
    title: 'Platforms',
  },
};

/** Is this slug one of the pages with shared hero copy? */
export function hasPageHero(slug: string): boolean {
  return Object.prototype.hasOwnProperty.call(PAGE_HEROES, slug);
}
